/**
 * Russian registration-plate input formats (GOST R 50577-2018 structure).
 * Color of the physical plate is not a format discriminator.
 * Keep in sync with apps/api/src/shared/ru-plate-formats.ts
 */

export const RU_PLATE_TYPE1 = 'RU_PLATE_TYPE1';
export const RU_PLATE_TYPE1A = 'RU_PLATE_TYPE1A';
export const RU_PLATE_TYPE1B = 'RU_PLATE_TYPE1B';
export const RU_PLATE_TYPE2 = 'RU_PLATE_TYPE2';
export const RU_PLATE_TYPE3 = 'RU_PLATE_TYPE3';

/**
 * Template policy: contractor chooses concrete plate type at fill time.
 * Not a mask itself — selected TYPE code or NONE is stored per field in booking draft.
 */
export const RU_PLATE_SELECTABLE = 'RU_PLATE_SELECTABLE';

/** Explicit free-text choice inside a SELECTABLE field (not a mask). */
export const RU_PLATE_RUNTIME_NONE = 'NONE';

/** @deprecated Prefer RU_PLATE_TYPE1 — kept for existing Package F / CFG-VAL imports. */
export const RU_PLATE_INPUT_FORMAT = RU_PLATE_TYPE1;

export const RU_PLATE_FORMAT_CODES = [
  RU_PLATE_TYPE1,
  RU_PLATE_TYPE1A,
  RU_PLATE_TYPE1B,
  RU_PLATE_TYPE2,
  RU_PLATE_TYPE3,
] as const;

/** Codes allowed in validation.input_format for STRING/TEXT. */
export const RU_PLATE_VALIDATION_INPUT_FORMATS = [
  ...RU_PLATE_FORMAT_CODES,
  RU_PLATE_SELECTABLE,
] as const;

export type RuPlateFormatCode = (typeof RU_PLATE_FORMAT_CODES)[number];

export type RuPlateRuntimeSelection = RuPlateFormatCode | typeof RU_PLATE_RUNTIME_NONE;

export type EffectiveRuPlateResolution =
  | { readonly kind: 'fixed'; readonly format: RuPlateFormatCode }
  | { readonly kind: 'selected'; readonly format: RuPlateFormatCode }
  | { readonly kind: 'free' }
  | { readonly kind: 'missing_selection' }
  | { readonly kind: 'invalid_selection'; readonly raw: string };

const ALLOWED_CYRILLIC = new Set('АВЕКМНОРСТУХ'.split(''));

const LATIN_TO_CYRILLIC: Record<string, string> = {
  A: 'А',
  B: 'В',
  E: 'Е',
  K: 'К',
  M: 'М',
  H: 'Н',
  O: 'О',
  P: 'Р',
  C: 'С',
  T: 'Т',
  Y: 'У',
  X: 'Х',
};

type SlotKind = 'L' | 'D';

export interface RuPlateFormatSpec {
  readonly code: RuPlateFormatCode;
  /** Admin / UX short label (RU). */
  readonly label: string;
  readonly example: string;
  readonly hint: string;
  /** Body slots before the region code. */
  readonly bodySlots: readonly SlotKind[];
  /** Allowed region digit counts — type 1/1A allow 2|3; others 2 only (no unconfirmed extension). */
  readonly regionLengths: readonly number[];
}

export const RU_PLATE_FORMAT_SPECS: Readonly<Record<RuPlateFormatCode, RuPlateFormatSpec>> = {
  [RU_PLATE_TYPE1]: {
    code: RU_PLATE_TYPE1,
    label: 'Обычный автомобиль (тип 1)',
    example: 'А123ВС77 / А123ВС777',
    hint: 'Одна буква, 3 цифры, 2 буквы, регион 2 или 3 цифры. Буквы только из набора АВЕКМНОРСТУХ.',
    bodySlots: ['L', 'D', 'D', 'D', 'L', 'L'],
    regionLengths: [2, 3],
  },
  [RU_PLATE_TYPE1A]: {
    code: RU_PLATE_TYPE1A,
    label: 'Обычный автомобиль, двухстрочный (тип 1А)',
    example: 'А123ВС77 / А123ВС777',
    hint: 'Линейная запись совпадает с типом 1; отличие только в двухстрочном форм-факторе знака.',
    bodySlots: ['L', 'D', 'D', 'D', 'L', 'L'],
    regionLengths: [2, 3],
  },
  [RU_PLATE_TYPE1B]: {
    code: RU_PLATE_TYPE1B,
    label: 'Жёлтый знак (тип 1Б: такси/регламентируемые перевозки)',
    example: 'АВ12377',
    hint: 'Две буквы, 3 цифры, регион 2 цифры. Цвет знака не задаёт формат — важна структура.',
    bodySlots: ['L', 'L', 'D', 'D', 'D'],
    regionLengths: [2],
  },
  [RU_PLATE_TYPE2]: {
    code: RU_PLATE_TYPE2,
    label: 'Прицеп / полуприцеп (тип 2)',
    example: 'АВ123477',
    hint: 'Две буквы, 4 цифры, регион 2 цифры.',
    bodySlots: ['L', 'L', 'D', 'D', 'D', 'D'],
    regionLengths: [2],
  },
  [RU_PLATE_TYPE3]: {
    code: RU_PLATE_TYPE3,
    label: 'Трактор / самоходная машина (тип 3)',
    example: '1234АВ77',
    hint: '4 цифры, 2 буквы, регион 2 цифры.',
    bodySlots: ['D', 'D', 'D', 'D', 'L', 'L'],
    regionLengths: [2],
  },
};

export type RuPlateCanonicalizeResult =
  | { readonly ok: true; readonly canonical: string }
  | { readonly ok: false; readonly code: string; readonly message: string };

export interface RuPlateMaskResult {
  readonly value: string;
  readonly caret: number;
}

export function isRuPlateFormatCode(value: unknown): value is RuPlateFormatCode {
  return typeof value === 'string' && (RU_PLATE_FORMAT_CODES as readonly string[]).includes(value);
}

export function isRuPlateRuntimeSelection(value: unknown): value is RuPlateRuntimeSelection {
  return value === RU_PLATE_RUNTIME_NONE || isRuPlateFormatCode(value);
}

export function isRuPlateSelectableInputFormat(validation: unknown): boolean {
  if (validation === null || typeof validation !== 'object' || Array.isArray(validation)) {
    return false;
  }
  const row = validation as Record<string, unknown>;
  return row.version === 1 && row.input_format === RU_PLATE_SELECTABLE;
}

/** Fixed TYPE* only — does not treat SELECTABLE as a mask. */
export function readRuPlateInputFormat(validation: unknown): RuPlateFormatCode | null {
  if (validation === null || typeof validation !== 'object' || Array.isArray(validation)) {
    return null;
  }
  const row = validation as Record<string, unknown>;
  if (row.version !== 1) {
    return null;
  }
  return isRuPlateFormatCode(row.input_format) ? row.input_format : null;
}

export function isRuPlateInputFormat(validation: unknown): boolean {
  return readRuPlateInputFormat(validation) !== null || isRuPlateSelectableInputFormat(validation);
}

/** Backward-compatible TYPE1-only predicate. */
export function isRuPlateType1InputFormat(validation: unknown): boolean {
  return readRuPlateInputFormat(validation) === RU_PLATE_TYPE1;
}

/**
 * Resolve the effective plate mask for a field value.
 * @param selectedRuntime code chosen at fill time (field_key → map); ignored for fixed formats.
 */
export function resolveEffectiveRuPlateFormat(
  validation: unknown,
  selectedRuntime: unknown,
): EffectiveRuPlateResolution {
  const fixed = readRuPlateInputFormat(validation);
  if (fixed !== null) {
    return { kind: 'fixed', format: fixed };
  }
  if (!isRuPlateSelectableInputFormat(validation)) {
    return { kind: 'free' };
  }
  if (selectedRuntime === null || selectedRuntime === undefined || selectedRuntime === '') {
    return { kind: 'missing_selection' };
  }
  if (selectedRuntime === RU_PLATE_RUNTIME_NONE) {
    return { kind: 'free' };
  }
  if (isRuPlateFormatCode(selectedRuntime)) {
    return { kind: 'selected', format: selectedRuntime };
  }
  return { kind: 'invalid_selection', raw: String(selectedRuntime) };
}

export function ruPlateMaxLength(format: RuPlateFormatCode): number {
  const spec = RU_PLATE_FORMAT_SPECS[format];
  const maxRegion = Math.max(...spec.regionLengths);
  return spec.bodySlots.length + maxRegion;
}

export function ruPlatePlaceholder(format: RuPlateFormatCode): string {
  return RU_PLATE_FORMAT_SPECS[format].example.split('/')[0]!.trim();
}

function mapLetterAtPosition(char: string): string {
  if (ALLOWED_CYRILLIC.has(char)) {
    return char;
  }
  return LATIN_TO_CYRILLIC[char] ?? char;
}

function acceptCharForSlot(char: string, slot: SlotKind): string | null {
  if (slot === 'L') {
    const mapped = mapLetterAtPosition(char);
    return ALLOWED_CYRILLIC.has(mapped) ? mapped : null;
  }
  return /^\d$/.test(char) ? char : null;
}

function bodyDigitSlice(normalized: string, bodySlots: readonly SlotKind[]): string {
  let digits = '';
  for (let i = 0; i < bodySlots.length; i += 1) {
    if (bodySlots[i] === 'D') {
      digits += normalized[i] ?? '';
    }
  }
  return digits;
}

function formatErrorMessage(format: RuPlateFormatCode): string {
  const spec = RU_PLATE_FORMAT_SPECS[format];
  return `Value does not match ${spec.label} (example ${spec.example})`;
}

/**
 * Canonicalize a complete plate for the given format.
 * Latin homoglyphs map to Cyrillic only in letter slots; digit slots never remap O/О→0.
 */
export function tryCanonicalizeRuPlate(
  format: RuPlateFormatCode,
  raw: string,
): RuPlateCanonicalizeResult {
  const trimmed = raw.trim();
  if (trimmed === '') {
    return { ok: false, code: 'RU_PLATE_EMPTY', message: 'Plate value is empty' };
  }
  const spec = RU_PLATE_FORMAT_SPECS[format];
  const compact = trimmed.replace(/[\s-]/g, '').toUpperCase();
  const maxLen = ruPlateMaxLength(format);
  const minLen = spec.bodySlots.length + Math.min(...spec.regionLengths);
  if (compact.length < minLen || compact.length > maxLen) {
    return { ok: false, code: 'RU_PLATE_SYNTAX', message: formatErrorMessage(format) };
  }
  const regionLen = compact.length - spec.bodySlots.length;
  if (!spec.regionLengths.includes(regionLen)) {
    return { ok: false, code: 'RU_PLATE_SYNTAX', message: formatErrorMessage(format) };
  }

  let out = '';
  for (let i = 0; i < spec.bodySlots.length; i += 1) {
    const char = compact[i]!;
    const accepted = acceptCharForSlot(char, spec.bodySlots[i]!);
    if (accepted === null) {
      return { ok: false, code: 'RU_PLATE_SYNTAX', message: formatErrorMessage(format) };
    }
    out += accepted;
  }
  const region = compact.slice(spec.bodySlots.length);
  if (!/^\d+$/.test(region)) {
    return { ok: false, code: 'RU_PLATE_SYNTAX', message: formatErrorMessage(format) };
  }
  out += region;

  const bodyDigits = bodyDigitSlice(out, spec.bodySlots);
  if (bodyDigits.length > 0 && /^0+$/.test(bodyDigits)) {
    return {
      ok: false,
      code: 'RU_PLATE_NUMBER',
      message: 'Plate number portion of all zeros is not allowed',
    };
  }
  if (/^0+$/.test(region)) {
    return {
      ok: false,
      code: 'RU_PLATE_REGION',
      message: 'Plate region of all zeros is not allowed',
    };
  }
  return { ok: true, canonical: out };
}

export function tryCanonicalizeRuPlateType1(raw: string): RuPlateCanonicalizeResult {
  return tryCanonicalizeRuPlate(RU_PLATE_TYPE1, raw);
}

/**
 * Live guided mask. Incomplete drafts allowed. Leading region zeros preserved (no numeric coercion).
 */
export function applyRuPlateMask(
  format: RuPlateFormatCode,
  nextRaw: string,
  previousValue: string,
  previousCaret: number,
): RuPlateMaskResult {
  const spec = RU_PLATE_FORMAT_SPECS[format];
  const maxLen = ruPlateMaxLength(format);
  const compactSource = nextRaw.replace(/[\s-]/g, '').toUpperCase();
  let value = '';
  for (const char of compactSource) {
    if (value.length >= maxLen) {
      break;
    }
    const slotIndex = value.length;
    const slot: SlotKind =
      slotIndex < spec.bodySlots.length ? spec.bodySlots[slotIndex]! : 'D';
    const accepted = acceptCharForSlot(char, slot);
    if (accepted !== null) {
      value += accepted;
    }
  }

  let caret: number;
  if (compactSource.length === 0) {
    caret = 0;
  } else if (value.length >= previousValue.length) {
    caret = value.length;
  } else {
    caret = Math.max(0, Math.min(value.length, previousCaret > 0 ? previousCaret - 1 : 0));
  }
  if (
    nextRaw.length > 0 &&
    Math.abs(nextRaw.replace(/[\s-]/g, '').length - previousValue.length) <= 1 &&
    previousCaret >= 0 &&
    previousCaret <= previousValue.length + 1
  ) {
    const delta = value.length - previousValue.length;
    caret = Math.max(0, Math.min(value.length, previousCaret + Math.max(delta, 0)));
    if (delta < 0) {
      caret = Math.max(0, Math.min(value.length, previousCaret + delta));
    }
  }
  return { value, caret };
}

export function applyRuPlateType1Mask(
  nextRaw: string,
  previousValue: string,
  previousCaret: number,
): RuPlateMaskResult {
  return applyRuPlateMask(RU_PLATE_TYPE1, nextRaw, previousValue, previousCaret);
}

export const RU_PLATE_TYPE1_MAX_LENGTH = ruPlateMaxLength(RU_PLATE_TYPE1);
export const RU_PLATE_TYPE1_PLACEHOLDER = ruPlatePlaceholder(RU_PLATE_TYPE1);

export function ruPlateUserFacingError(format: RuPlateFormatCode): string {
  const spec = RU_PLATE_FORMAT_SPECS[format];
  return `Укажите госномер в формате «${spec.label}», например ${spec.example}.`;
}
